import { formatPlainDate, toShortTime } from '../../core/dates/plain-date';
import { useTranslation } from '../../core/hooks/use-translation';
import type { MeetingFormat } from '../../core/models/meeting-format';
import { CalendarIcon, ClockIcon, MapPinIcon, MonitorIcon, PackageIcon, UsersIcon } from './booking-icons';

interface BookingSummaryProps {
    serviceName: string;
    /** The consultant the customer picked, or null when they left the assignment to staff. */
    specialistName: string | null;
    format: MeetingFormat;
    /** `"YYYY-MM-DD"`, null for an online request, which holds no time. */
    date: string | null;
    /** The wire value, `"HH:mm:ss"`; shortened for display. */
    time: string | null;
    /** `sum-bar` above the contact fields, `ok-chips` on the confirmation screen. */
    variant: 'bar' | 'chips';
}

/**
 * What the customer has chosen so far, as chips — above the contact fields on step three, and again
 * on the confirmation screen.
 *
 * It is one component for both because they are the same four facts in the same four shapes; the
 * live page builds each of them from its own string of HTML, which is how the two came to disagree
 * about whether an online request shows a date at all.
 *
 * The date is rendered from the `"YYYY-MM-DD"` string. The live confirmation does
 * `new Date(appointmentDate).getDate()`, which reads UTC midnight in the device's zone and shows
 * any browser west of Greenwich **the previous day** — on the screen that confirms a booking.
 */
export function BookingSummary({ serviceName, specialistName, format, date, time, variant }: BookingSummaryProps) {
    const { t, language } = useTranslation();
    const online = format === 'Online';
    const chip = variant === 'bar' ? 'sum-chip' : 'ok-chip';

    return (
        <div className={variant === 'bar' ? 'sum-bar' : 'ok-chips'}>
            {/* Empty only while the service list is still in flight on a reloaded step 3 — a chip
                showing an icon and nothing else says less than no chip at all. */}
            {serviceName !== '' && (
                <span className={chip}>
                    <PackageIcon />
                    {serviceName}
                </span>
            )}

            {/* No chip when nobody was picked: "we will assign one" is what every request used to
                say, and a chip saying it would read as a choice the customer made. */}
            {specialistName !== null && (
                <span className={chip}>
                    <UsersIcon />
                    {specialistName}
                </span>
            )}

            <span className={online ? `${chip} online-chip` : chip}>
                {online ? <MonitorIcon /> : <MapPinIcon />}
                {t(online ? 'booking.format.online' : 'booking.format.in_person')}
            </span>

            {date !== null && (
                <span className={chip}>
                    <CalendarIcon />
                    {formatPlainDate(date, language)}
                </span>
            )}

            {time !== null && (
                <span className={chip}>
                    <ClockIcon />
                    {toShortTime(time)}
                </span>
            )}
        </div>
    );
}
